import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { directory, supervision, supervisionBookings } from '@cwos/api-client';
import { Pill, SuccessEmpty } from '@cwos/ui-admin';
import { SettingsCard, SettingsField } from '../settings/common.js';
import {
  FORMAT_LABELS,
  formatCalendarDate,
  formatWhen,
  messageOf,
  nowLocalInput,
  toDatetimeLocalInput,
} from './supervisionLogic.js';
import {
  CLOSE_OUTCOME_LABELS,
  EMPTY_CLOSE_DRAFT,
  GROUP_HEADING,
  MOMENT_VARIANT,
  bookingGroup,
  bookingPayload,
  confirmDraftFrom,
  confirmPayload,
  confirmRefusalSentence,
  emptyBookingDraft,
  mayOfferBooking,
  nextDueSentence,
  validateBookingDraft,
  validateCloseDraft,
  validateConfirmDraft,
  whenSentence,
  type BookingDraft,
  type CloseDraft,
  type ConfirmDraft,
} from './supervisionBookingLogic.js';

/**
 * ===========================================================================
 * SUPERVISION BOOKINGS — A CALENDAR EVENT IS NOT SUPERVISION COMPLETION
 * ===========================================================================
 *
 * Four routes have existed since migration 1758110000000 — book, mark occurred,
 * confirm, close — with a database CHECK constraint behind them, an attestation
 * rule refusing organization-wide management, and NO CLIENT BINDING AT ALL. A
 * supervisor could not book supervision, say a meeting happened, confirm one, or
 * record a no-show from any screen in the product.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS THE MANAGEMENT SURFACE AND NOT THE WORKER'S
 * ---------------------------------------------------------------------------
 * `OPERATIONAL-LIBRARY-V1.md` §2.1: management sees intelligence, workers see
 * priorities — and the founder's Ops directive says in as many words that
 * workers should not see an operations control center. The two personas are
 * genuinely different here rather than differently permissioned:
 *
 *   A SUPERVISOR is owed WHOSE SUPERVISION IS OUTSTANDING. That is a queue of
 *   other people's obligations, and every act on it — booking, confirming,
 *   closing — changes somebody else's compliance record. It belongs here.
 *
 *   A SUPERVISEE is owed WHEN THEIR SUPERVISION IS, what to bring, and what
 *   happened. That is one person's own record and no acts at all, because a
 *   supervisee confirming their own supervision is the fabrication
 *   `assertMayConfirm` exists to refuse. It lives in the worker app
 *   (`apps/worker-mobile/src/components/MySupervision.tsx`) and shares only the
 *   READING of a row with this screen, through
 *   `supervisionBookings.supervisionMoment`, so the two cannot tell one person
 *   a booking passed and another that it completed.
 *
 * ---------------------------------------------------------------------------
 * THE THREE STATES THIS SCREEN EXISTS TO KEEP APART
 * ---------------------------------------------------------------------------
 *   A BOOKING THAT PASSED WITHOUT A SESSION is its own answer. It is not a
 *   completed session and it is not nothing — it is a supervision slot that
 *   went by unaccounted for, and it gets the loudest heading on the screen.
 *
 *   A SESSION RECORDED WITH NO BOOKING is legitimate and is not surfaced here
 *   as an exception at all. Supervision happens unscheduled.
 *
 *   A BOOKING IN THE FUTURE is neither, and sits under "Still to come" where
 *   nothing about it reads as late.
 *
 * ---------------------------------------------------------------------------
 * NO PERMISSION KEY IS INVENTED, AND THE REASON IS NOT CONVENIENCE
 * ---------------------------------------------------------------------------
 * All four routes charge `supervision:record`, the same key the direct-record
 * door uses, because putting supervision on somebody's compliance record is one
 * authority regardless of which door it comes through. The narrowing that
 * decides who may CONFIRM is not a key at all — it is the `supervisor_worker_id`
 * edge — so this screen resolves the viewer's own worker row and offers Confirm
 * on that basis. A holder of `supervision:manage` gets the refusal SENTENCE
 * rather than a hidden button, because a manager who finds no control concludes
 * the product is broken, and the policy is the interesting part.
 */

type Group = 'unaccounted' | 'to_confirm' | 'upcoming';
const GROUP_ORDER: Group[] = ['unaccounted', 'to_confirm', 'upcoming'];

export interface SupervisionBookingsProps {
  workerId: string;
  worker: supervision.WorkerComplianceView;
  policies: supervision.WorkerDetail['policies'];
  sessions: supervision.SessionView[];
  /** `supervision:record`, or `supervision:manage` which implies it. */
  canRecord: boolean;
  /** `supervision:manage` — org-wide, and expressly NOT enough to confirm. */
  managesOrgWide: boolean;
}

export function SupervisionBookings(props: SupervisionBookingsProps) {
  const { workerId, worker, policies, sessions, canRecord, managesOrgWide } = props;
  const qc = useQueryClient();

  /**
   * THE VIEWER'S OWN WORKER ROW — not their permission set.
   *
   * `/auth/me` carries permissions and no worker id, and the question "may I
   * confirm this booking?" is not answerable from a permission at all. This is
   * the only read that can answer it, and its three states are kept apart: a
   * read that has not arrived, or failed, must never render as "you are not the
   * supervisor" — that would tell the one person who CAN confirm that they may
   * not, on a bad connection.
   */
  const self = useQuery({
    queryKey: ['directory', 'me'],
    queryFn: () => directory.me(),
    enabled: canRecord,
  });
  const viewerWorkerId = self.data?.id ?? null;

  const [booking, setBooking] = useState(false);
  const [draft, setDraft] = useState<BookingDraft>(() =>
    emptyBookingDraft(nowLocalInput(new Date(Date.now() + 3_600_000))),
  );
  const [closingId, setClosingId] = useState<string | null>(null);
  const [closeDraft, setCloseDraft] = useState<CloseDraft>(EMPTY_CLOSE_DRAFT);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [confirmDraft, setConfirmDraft] = useState<ConfirmDraft>({
    occurredAt: '',
    durationMinutes: '60',
  });
  const [error, setError] = useState<string | null>(null);
  const [supervisorChoice, setSupervisorChoice] = useState('');

  const assignedSupervisorId = worker.supervisorWorkerId;
  const effectiveSupervisorId = managesOrgWide
    ? supervisorChoice || (assignedSupervisorId ?? '')
    : (assignedSupervisorId ?? '');

  const supervisorOptions = useQuery({
    queryKey: ['supervision', 'workers', 'all'],
    queryFn: () => supervision.workers({ limit: 500 }),
    enabled: managesOrgWide && booking,
  });

  function reset() {
    setBooking(false);
    setClosingId(null);
    setConfirmingId(null);
    setCloseDraft(EMPTY_CLOSE_DRAFT);
    setError(null);
    setSupervisorChoice('');
    setDraft(emptyBookingDraft(nowLocalInput(new Date(Date.now() + 3_600_000))));
  }

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ['supervision'] });
  }

  const create = useMutation({
    mutationFn: () =>
      supervisionBookings.book(bookingPayload(workerId, effectiveSupervisorId, draft)),
    onSuccess: () => {
      reset();
      invalidate();
    },
    onError: (err: unknown) => setError(messageOf(err)),
  });

  const occurred = useMutation({
    mutationFn: (id: string) =>
      supervisionBookings.markOccurred(id, { occurredAt: new Date().toISOString() }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (err: unknown) => setError(messageOf(err)),
  });

  /**
   * THE ATTESTATION. It carries the instant and the length THE SUPERVISOR
   * STATED, never the ones the calendar proposed.
   *
   * The form's defaults come from the booking, and that is all they are. A
   * one-press Confirm that posted `row.occurredAt` and `row.durationMinutes`
   * would attest to a time and a length nobody had said out loud — the booked
   * ones, because `insertBooking` writes the plan into `occurred_at` — which is
   * "a calendar event is supervision completion" with a button on it.
   */
  const confirmIt = useMutation({
    mutationFn: (id: string) => supervisionBookings.confirm(id, confirmPayload(confirmDraft)),
    onSuccess: () => {
      reset();
      invalidate();
    },
    onError: (err: unknown) => setError(messageOf(err)),
  });

  const closeIt = useMutation({
    mutationFn: (id: string) =>
      supervisionBookings.close(id, {
        outcome: closeDraft.outcome,
        reason: closeDraft.reason.trim(),
      }),
    onSuccess: () => {
      reset();
      invalidate();
    },
    onError: (err: unknown) => setError(messageOf(err)),
  });

  /**
   * ONE `now` FOR EVERY ROW ON THE SCREEN.
   *
   * Two bookings a second apart must not land on opposite sides of "has the
   * time passed" because the second one was read from a later clock. Memoized
   * on the session list, so it re-derives when the data changes and not on
   * every keystroke in the form above it.
   */
  const grouped = useMemo(() => {
    const now = new Date();
    const out: Record<
      Group,
      Array<{
        row: supervision.SessionView;
        moment: ReturnType<typeof supervisionBookings.supervisionMoment>;
      }>
    > = { unaccounted: [], to_confirm: [], upcoming: [] };
    for (const row of sessions) {
      const moment = supervisionBookings.supervisionMoment(row, now);
      const group = bookingGroup(moment);
      if (group) out[group].push({ row, moment });
    }
    return out;
  }, [sessions]);

  const openCount =
    grouped.unaccounted.length + grouped.to_confirm.length + grouped.upcoming.length;

  const requirementName = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of policies) {
      if (p.requirementId && p.requirementName) m.set(p.requirementId, p.requirementName);
    }
    return m;
  }, [policies]);

  const draftErrors = validateBookingDraft(draft, new Date().toISOString());
  const confirmErrors = validateConfirmDraft(confirmDraft, new Date().toISOString());
  const canSubmitBooking =
    Object.keys(draftErrors).length === 0 && effectiveSupervisorId !== '' && !create.isPending;
  const closeErrors = validateCloseDraft(closeDraft);

  const offerBooking = mayOfferBooking({
    canRecord,
    managesOrgWide,
    assignedSupervisorWorkerId: assignedSupervisorId,
  });

  const nextDue = nextDueSentence(worker, (iso) => formatCalendarDate(iso));

  return (
    <SettingsCard>
      <div className="rn-card-head">
        <h2 className="rn-card-title">Booked supervision</h2>
        {offerBooking && !booking && closingId === null ? (
          <button
            type="button"
            className="rn-btn-quiet"
            onClick={() => {
              setBooking(true);
              setError(null);
              setSupervisorChoice(assignedSupervisorId ?? '');
            }}
          >
            Book supervision
          </button>
        ) : null}
      </div>

      {/*
        A BOOKING IS NOT COMPLIANCE, said once, at the top, in the product's own
        voice. This is not decoration: the four statuses below all belong to a
        row in `supervision_session`, the same table a completed session lives
        in, and a reader who has just been shown four "supervision" rows needs
        to know that none of them has satisfied anything.
      */}
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        A booking is a plan. Supervision counts only when the supervisor who delivered it confirms
        it happened.
      </p>

      {/*
        WHEN THE NEXT ONE IS DUE — OR THE REFUSAL, SPELLED AS ITSELF.

        `nextDueOn` is null for an organization that has declared no supervision
        requirement reaching this worker, and the engine now answers
        `insufficient_context` rather than measuring them against an invented
        45-day default. A booking screen that filled that silence with a date
        would put the default back on the client side, which is where it would
        be hardest to find.
      */}
      <p
        className="rn-muted"
        style={{
          marginTop: 'var(--space-4)',
          ...(nextDue.kind === 'no_expectation'
            ? { color: 'var(--color-unsure-deep)' }
            : undefined),
        }}
      >
        {nextDue.kind === 'no_expectation' ? `Cannot be determined. ${nextDue.text}` : nextDue.text}
      </p>

      {booking ? (
        <form
          className="org-create"
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmitBooking) return;
            create.mutate();
          }}
        >
          <p className="rn-muted" style={{ fontSize: 13 }}>
            Booking supervision records an intention. It satisfies nothing until it is confirmed.
          </p>
          <div className="org-create-grid">
            <SettingsField label="Booked for" error={draftErrors['scheduledFor']}>
              <input
                style={{ marginBottom: 0 }}
                type="datetime-local"
                value={draft.scheduledFor}
                onChange={(e) => setDraft({ ...draft, scheduledFor: e.target.value })}
              />
            </SettingsField>
            <SettingsField label="Duration (minutes)" error={draftErrors['durationMinutes']}>
              <input
                style={{ marginBottom: 0 }}
                type="number"
                min={1}
                max={1440}
                value={draft.durationMinutes}
                onChange={(e) => setDraft({ ...draft, durationMinutes: e.target.value })}
              />
            </SettingsField>
          </div>
          <div className="org-create-grid">
            <SettingsField label="Format">
              <select
                style={{ marginBottom: 0 }}
                value={draft.format}
                onChange={(e) =>
                  setDraft({ ...draft, format: e.target.value as BookingDraft['format'] })
                }
              >
                <option value="in_person">In person</option>
                <option value="video">Video</option>
                <option value="phone">Phone</option>
                <option value="group">Group</option>
              </select>
            </SettingsField>
            {policies.length > 0 ? (
              <SettingsField label="Counts toward (optional)">
                <select
                  style={{ marginBottom: 0 }}
                  value={draft.requirementId}
                  onChange={(e) => setDraft({ ...draft, requirementId: e.target.value })}
                >
                  <option value="">Not linked to a specific requirement</option>
                  {policies
                    .filter((p) => p.requirementId)
                    .map((p) => (
                      <option key={p.requirementId} value={p.requirementId!}>
                        {p.requirementName}
                      </option>
                    ))}
                </select>
              </SettingsField>
            ) : null}
          </div>
          <SettingsField
            label="Calendar reference (optional)"
            error={draftErrors['calendarEventRef']}
          >
            <input
              style={{ marginBottom: 0 }}
              type="text"
              maxLength={256}
              value={draft.calendarEventRef}
              onChange={(e) => setDraft({ ...draft, calendarEventRef: e.target.value })}
            />
          </SettingsField>
          {/*
            SAID WHERE THE FIELD IS. A calendar reference is stored, traceable
            and read by NOTHING that decides whether an obligation is met — and
            an operator who pastes one in has every reason to assume otherwise.
          */}
          <p className="rn-muted" style={{ fontSize: 13 }}>
            Stored so this booking can be traced back to your calendar. It does not make the
            supervision count.
          </p>

          {managesOrgWide ? (
            <SettingsField label="To be delivered by">
              <select
                style={{ marginBottom: 0 }}
                value={supervisorChoice}
                onChange={(e) => setSupervisorChoice(e.target.value)}
              >
                <option value="">Choose who will deliver this</option>
                {(supervisorOptions.data?.workers ?? [])
                  .filter((w) => w.workerId !== workerId)
                  .map((w) => (
                    <option key={w.workerId} value={w.workerId}>
                      {w.displayName}
                    </option>
                  ))}
              </select>
              {/*
                AN EMPTY PICKER IS NOT A WORKFORCE OF NOBODY. If the roster read
                failed, the select renders with one option and no explanation,
                which reads as "there is nobody who could deliver this".
              */}
              {supervisorOptions.isError ? (
                <p className="rn-muted" style={{ fontSize: 13 }}>
                  Phibian could not load the list of people who could deliver this just now, so it
                  is empty rather than complete. Try again shortly.
                </p>
              ) : null}
            </SettingsField>
          ) : (
            <p className="rn-muted" style={{ fontSize: 13 }}>
              Booked as delivered by {worker.supervisorName ?? 'you'} — you can only book
              supervision you will deliver yourself.
            </p>
          )}

          {error ? (
            <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
              {error}
            </p>
          ) : null}

          <div className="set-actions">
            <button
              type="submit"
              className="signin-devbtn"
              style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
              disabled={!canSubmitBooking}
            >
              {create.isPending ? 'Booking…' : 'Book supervision'}
            </button>
            <button type="button" className="rn-btn-quiet" onClick={reset}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {!booking && error ? (
        <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
          {error}
        </p>
      ) : null}

      {openCount === 0 && !booking ? (
        <SuccessEmpty
          heading="No open bookings"
          message="Nothing is booked for this worker. Supervision recorded without a booking still counts."
        />
      ) : null}

      {GROUP_ORDER.map((group) =>
        grouped[group].length === 0 ? null : (
          <section key={group} style={{ marginTop: 'var(--space-16)' }}>
            <h3 className="rn-card-title" style={{ fontSize: 15 }}>
              {GROUP_HEADING[group]}
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {grouped[group].map(({ row, moment }) => {
                const canConfirm = supervisionBookings.mayConfirm(row, viewerWorkerId);
                const canClose = supervisionBookings.mayClose(row, viewerWorkerId, managesOrgWide);
                return (
                  <li
                    key={row.id}
                    style={{
                      borderTop: '1px solid var(--color-rule)',
                      padding: 'var(--space-10) 0',
                    }}
                  >
                    <div className="cmp-control-head">
                      <div>
                        {/*
                          THE VERB IS PART OF THE TIMESTAMP. `occurred_at` on a
                          booking is the BOOKED instant — the column is NOT NULL
                          so the insert fills it with the plan — and printing it
                          bare says supervision happened at a time nobody has
                          claimed it did.
                        */}
                        <div>{whenSentence(moment, formatWhen(moment.instant))}</div>
                        <div className="rn-muted" style={{ fontSize: 13 }}>
                          {row.supervisorName ?? 'Supervisor not named'} · {row.durationMinutes} min
                          · {FORMAT_LABELS[row.format]}
                          {row.requirementId
                            ? ` · ${requirementName.get(row.requirementId) ?? 'a requirement'}`
                            : ''}
                        </div>
                      </div>
                      <Pill variant={MOMENT_VARIANT[moment.kind]} dot>
                        {moment.label}
                      </Pill>
                    </div>
                    <p className="rn-muted" style={{ fontSize: 13, marginTop: 'var(--space-4)' }}>
                      {moment.meaning}
                    </p>
                    {row.calendarEventRef ? (
                      <p className="rn-muted" style={{ fontSize: 13 }}>
                        From your calendar ({row.calendarEventRef}). A calendar entry does not make
                        supervision count.
                      </p>
                    ) : null}

                    {confirmingId === row.id ? (
                      <form
                        className="org-create"
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (Object.keys(confirmErrors).length > 0) return;
                          confirmIt.mutate(row.id);
                        }}
                      >
                        {/*
                          THE SENTENCE THAT MAKES THIS AN ATTESTATION. It is
                          shown above the fields rather than beside the button,
                          because it is what the fields are FOR.
                        */}
                        <p className="rn-muted" style={{ fontSize: 13 }}>
                          Confirming says you delivered this supervision and that it happened. The
                          times below are what was booked — change them if it ran differently.
                        </p>
                        <div className="org-create-grid">
                          <SettingsField
                            label="When it actually happened"
                            error={confirmErrors['occurredAt']}
                          >
                            <input
                              style={{ marginBottom: 0 }}
                              type="datetime-local"
                              value={confirmDraft.occurredAt}
                              onChange={(e) =>
                                setConfirmDraft({ ...confirmDraft, occurredAt: e.target.value })
                              }
                            />
                          </SettingsField>
                          <SettingsField
                            label="How long it ran (minutes)"
                            error={confirmErrors['durationMinutes']}
                          >
                            <input
                              style={{ marginBottom: 0 }}
                              type="number"
                              min={1}
                              max={1440}
                              value={confirmDraft.durationMinutes}
                              onChange={(e) =>
                                setConfirmDraft({
                                  ...confirmDraft,
                                  durationMinutes: e.target.value,
                                })
                              }
                            />
                          </SettingsField>
                        </div>
                        <div className="set-actions">
                          <button
                            type="submit"
                            className="signin-devbtn"
                            style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                            disabled={Object.keys(confirmErrors).length > 0 || confirmIt.isPending}
                          >
                            {confirmIt.isPending ? 'Confirming…' : 'Confirm this happened'}
                          </button>
                          <button type="button" className="rn-btn-quiet" onClick={reset}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : closingId === row.id ? (
                      <form
                        className="org-create"
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (Object.keys(closeErrors).length > 0) return;
                          closeIt.mutate(row.id);
                        }}
                      >
                        <SettingsField label="What happened">
                          <select
                            style={{ marginBottom: 0 }}
                            value={closeDraft.outcome}
                            onChange={(e) =>
                              setCloseDraft({
                                ...closeDraft,
                                outcome: e.target.value as CloseDraft['outcome'],
                              })
                            }
                          >
                            <option value="cancelled">{CLOSE_OUTCOME_LABELS.cancelled}</option>
                            <option value="no_show">{CLOSE_OUTCOME_LABELS.no_show}</option>
                          </select>
                        </SettingsField>
                        <SettingsField label="Why" error={closeErrors['reason']}>
                          <input
                            style={{ marginBottom: 0 }}
                            type="text"
                            maxLength={500}
                            value={closeDraft.reason}
                            onChange={(e) =>
                              setCloseDraft({ ...closeDraft, reason: e.target.value })
                            }
                          />
                        </SettingsField>
                        <p className="rn-muted" style={{ fontSize: 13 }}>
                          Closing a booking does not close the obligation. The supervision is still
                          owed, and this worker will still show as due.
                        </p>
                        <div className="set-actions">
                          <button
                            type="submit"
                            className="signin-devbtn"
                            style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                            disabled={Object.keys(closeErrors).length > 0 || closeIt.isPending}
                          >
                            {closeIt.isPending ? 'Closing…' : 'Close this booking'}
                          </button>
                          <button type="button" className="rn-btn-quiet" onClick={reset}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className="set-actions" style={{ marginTop: 'var(--space-6)' }}>
                        {canConfirm ? (
                          <button
                            type="button"
                            className="rn-btn-quiet"
                            onClick={() => {
                              setConfirmingId(row.id);
                              setClosingId(null);
                              setBooking(false);
                              setError(null);
                              setConfirmDraft(
                                confirmDraftFrom({
                                  occurredAt: row.occurredAt,
                                  durationMinutes: row.durationMinutes,
                                  scheduledFor: row.scheduledFor,
                                  toLocalInput: toDatetimeLocalInput,
                                }),
                              );
                            }}
                          >
                            Confirm it happened
                          </button>
                        ) : null}
                        {/*
                          THREE STATES OF ONE READ, AND ONLY ONE OF THEM IS A
                          REFUSAL.

                          `viewerWorkerId` is null in three different
                          situations — the read has not landed, the read
                          failed, and the read landed and this viewer is
                          somebody else — and `mayConfirm` cannot tell them
                          apart, because it is asked about a worker id and a
                          missing id is a missing id. Rendering the policy
                          sentence off all three would tell the supervisor of
                          record they may not confirm their own session because
                          a request was slow, which is the worst direction for
                          this particular sentence: it is the one person whose
                          confirmation is the correct one.

                          Spelled `!self.isLoading && !self.isError && …`, the
                          resolution check FIRST, which is how the five screens
                          that already got this right spell it.
                        */}
                        {canRecord &&
                        !canConfirm &&
                        !self.isLoading &&
                        !self.isError &&
                        moment.needsAnAnswer ? (
                          <p className="rn-muted" style={{ fontSize: 13 }}>
                            {confirmRefusalSentence(row.supervisorName)}
                          </p>
                        ) : null}
                        {canRecord && !canConfirm && self.isLoading && moment.needsAnAnswer ? (
                          <p className="rn-muted" style={{ fontSize: 13 }}>
                            Checking whether you are the supervisor of record for this booking.
                          </p>
                        ) : null}
                        {canRecord && !canConfirm && self.isError && moment.needsAnAnswer ? (
                          <p className="rn-muted" style={{ fontSize: 13 }}>
                            Phibian could not check whether you are the supervisor of record for
                            this booking, so confirming is not offered here. Nothing about the
                            booking has changed.
                          </p>
                        ) : null}
                        {canConfirm && moment.kind !== 'unconfirmed' ? (
                          <button
                            type="button"
                            className="rn-btn-quiet"
                            disabled={occurred.isPending}
                            onClick={() => occurred.mutate(row.id)}
                          >
                            It happened — confirm later
                          </button>
                        ) : null}
                        {canClose ? (
                          <button
                            type="button"
                            className="rn-btn-quiet"
                            onClick={() => {
                              setClosingId(row.id);
                              setCloseDraft(EMPTY_CLOSE_DRAFT);
                              setBooking(false);
                              setError(null);
                            }}
                          >
                            It did not happen
                          </button>
                        ) : null}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ),
      )}
    </SettingsCard>
  );
}
